// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";

// Styles
import { getFeatureRowLabelColor } from "src/styles/mui/muiStylesHelper";

const VisualField = () => {
  const { t } = useTranslation("operating-manual");
  const theme = useTheme();
  const rowLabelColor = getFeatureRowLabelColor(theme);

  const featureTableContent = [
    {
      title: t("Test pattern"),
      content: t(
        "Suprathreshold (Screening): RLFast, Full 120, Esterman. Threshold: Fast, Standard, Full. See table below for more detail"
      ),
    },
    {
      title: t("Visual field grids"),
      content: t(
        "24-2 (54 points, 24 degrees), 10-2 (68 points, 10 degrees), 30-2 (76 points, 30 degrees), 24-2C (64 points, 24 degrees), Full 120 (120 points, 120 degrees), Esterman (120 points, 160 degrees bitemporal), C40 (40 points, 60 degrees), Superior 36 (36 points, 120 degrees), C20 (20 points, 24 degrees)"
      ),
    },
    {
      title: t("Report data"),
      content: t(
        "Mean deviation (MD), pattern standard deviation (PSD), RetinaLogik field index (RLFI), Glaucoma Hemifield Test (GHT), Fovea measurement"
      ),
    },
    {
      title: t("Stimulus color"),
      content: t("Red or white"),
    },
    {
      title: t("Stimulus size"),
      content: t(
        "Size III (smallest diameter), Size IV, and Size V (largest diameter). Esterman uses Size III"
      ),
    },
    {
      title: t("Stimulus duration"),
      content: t("300 msec - 500 msec"),
    },
    {
      title: t("Stimulus intensity"),
      content: t(
        "Stimulus intensity can be set between 8 and 32 dB for visual field screening exams"
      ),
    },
    {
      title: t("Fixation"),
      content: t("Sphere shape or Diamond shape"),
    },
    {
      title: t("Fixation method"),
      content: t(
        "Eye tracking (providing a gaze tracking report at the end) or Blind Spot Monitoring (displaying a stimulus in the blind spot at random times during the examination, where a positive response may indicate that the patient is not fixated)"
      ),
    },
    {
      title: t("Fixation cues"),
      content: t(
        "Fixation will visually change color from blue to red if patient is not fixated, and system will provide audio reminders to remain focused if patient is fixated"
      ),
    },
    {
      title: t("Eye selection"),
      content: t(
        "Both (will test one at a time), Left (will only test left), Right (will only test right)"
      ),
    },
    {
      title: t("Auto-pause"),
      content: t(
        "Automatically pause the examination if the patient loses fixation three times during the examination (Optional)"
      ),
    },
    {
      title: t("Calibration"),
      content: t(
        "Performs calibration of the eye tracking system before beginning the exam (Optional)"
      ),
    },
  ];

  const patternTableContent = [
    {
      title: t("RLFast"),
      classification: t("Monocular"),
      avgTestTime: t("0:55 minutes per eye (24-2)"),
      content: t(
        "Suprathreshold examination available in 24-2, 10-2, 30-2, 24-2C, C20 and C40 grids"
      ),
    },
    {
      title: t("Fast"),
      classification: t("Monocular"),
      avgTestTime: t("2:30 minutes per eye (24-2)"),
      content: t(
        "Thresholding exam available in 24-2, 10-2, 30-2, and 24-2C grids. Our thresholding algorithm uses a staircase approach with our age corrected data and stats to better predict starting points. This is one of our proprietary algorithms. It is usually faster than standard but less precise."
      ),
    },
    {
      title: t("Standard"),
      classification: t("Monocular"),
      avgTestTime: t("3:00 minutes per eye (24-2)"),
      content: t(
        "Thresholding exam available in 24-2, 10-2, 30-2, and 24-2C grids. Our thresholding algorithm uses a staircase approach with our age corrected data and stats to better predict starting points. This is our proprietary algorithm."
      ),
    },
    {
      title: t("Full Threshold"),
      classification: t("Monocular"),
      avgTestTime: t("3:30 minutes per eye (24-2)"),
      content: t(
        "Thresholding exam available in 24-2, 10-2, 30-2, and 24-2C grids. Our thresholding algorithm uses a staircase approach to finalize the stimulus intensity the patient can see at a specific location."
      ),
    },
    {
      title: t("Full 120"),
      classification: t("Monocular"),
      avgTestTime: t("2:40 minutes"),
      content: t(
        "Suprathreshold examination at a default value of 10 dB stimulus intensity, 120 degrees"
      ),
    },
    {
      title: t("Esterman"),
      classification: t("Binocular"),
      avgTestTime: t("2:40 minutes"),
      content: t(
        "Suprathreshold examination at a default value of 10 dB stimulus intensity, 160 degrees bitemporal"
      ),
    },
    {
      title: t("Superior 36"),
      classification: t("Monocular"),
      avgTestTime: t("1:20 minutes per eye"),
      content: t(
        "Suprathreshold examination at a default value of 10 dB stimulus intensity, 36 points, 120 degrees horizontal"
      ),
    },
  ];

  const getTableRow = useCallback(
    (title: string, content: string) => (
      <Grid container key={title}>
        <Grid
          item
          xs={6}
          sm={3}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <Typography color={rowLabelColor} fontSize="14px" fontWeight={600}>
            {t(title)}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          sm={9}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <Typography fontSize="14px">{t(content)}</Typography>
        </Grid>
      </Grid>
    ),
    [t, rowLabelColor]
  );

  const getTestPatternTableRow = useCallback(
    (
      title: string,
      classification: string,
      avgTestTime: string,
      content: string
    ) => (
      <Grid container key={title}>
        <Grid
          item
          xs={6}
          sm={3}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <Typography color={rowLabelColor} fontSize="14px" fontWeight={600}>
            {t(title)}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          sm={9}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Typography fontSize="14px">
            <span style={{ fontWeight: 600 }}>{t("Classification")}: </span>
            {t(classification)}
          </Typography>
          <Typography fontSize="14px" mb="8px">
            <span style={{ fontWeight: 600 }}>{t("Average test time")}: </span>
            {t(avgTestTime)}
          </Typography>
          <Typography fontSize="14px">{t(content)}</Typography>
        </Grid>
      </Grid>
    ),
    [t, rowLabelColor]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <DefaultCard>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Grid container>
            <Grid
              item
              xs={6}
              sm={3}
              sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <Typography fontWeight={600}>{t("Features")}</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              sm={9}
              sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <Typography fontWeight={600}>{t("Description")}</Typography>
            </Grid>
          </Grid>

          {featureTableContent.map((tableItem) =>
            getTableRow(tableItem.title, tableItem.content)
          )}
        </Box>
      </DefaultCard>
      <DefaultCard>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Grid container>
            <Grid
              item
              xs={6}
              sm={3}
              sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <Typography fontWeight={600}>{t("Test Pattern")}</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              sm={9}
              sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <Typography fontWeight={600}>{t("Description")}</Typography>
            </Grid>
          </Grid>

          {patternTableContent.map((tableItem) =>
            getTestPatternTableRow(
              tableItem.title,
              tableItem.classification,
              tableItem.avgTestTime,
              tableItem.content
            )
          )}
        </Box>
      </DefaultCard>
    </Box>
  );
};

export default VisualField;
